import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Badge } from '../components/common/Badge';
import { Modal } from '../components/common/Modal';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import {
  Plus, Trash2, Edit2, RefreshCw, CheckCircle, AlertTriangle, Zap,
  Activity, Wifi, WifiOff, Server, Shield, Clock, Copy, Save, Plug,
} from 'lucide-react';
import * as api from '../services/api';
import PageHeader from '../components/common/PageHeader';
import {
  BRAND, S, ACCENT,
  GovStyles, GovPageLayout, StatCard, StatusBadge,
  Card, SearchInput, RefreshButton, PrimaryButton, GhostButton,
  EmptyState, ErrorBanner, LoadingSkeleton, Badge as DesignSystemBadge, SectionLabel,
  btnBase, inputBase,
} from '../components/common/GovernanceDesignSystem';

// ---------------------------------------------------------------------------
// Helper: unwrap apiFetch response — handles both {ok, data} and raw arrays
// ---------------------------------------------------------------------------
const unwrap = (res) => {
  if (res && typeof res === 'object' && 'data' in res) return res.data;
  return res;
};

// ---------------------------------------------------------------------------
// DOMAIN-SPECIFIC CONSTANTS (kept from original)
// ---------------------------------------------------------------------------

// External Agent Connectors are ONLY for agent building platforms.
// Keys match backend PlatformConnector.platformType() values (UPPER_SNAKE_CASE).
const PLATFORM_LABELS = {
  VERTEX_AI:    'Vertex AI (Google)',
  ANTHROPIC:    'Anthropic',
  OPENAI:       'OpenAI',
  AGENTFORCE:   'AgentForce (Salesforce)',
  SERVICENOW:   'ServiceNow',
  MICROSOFT:    'Microsoft Azure AI',
  CREWAI:       'CrewAI',
  LANGCHAIN:    'LangChain',
  LANGGRAPH:    'LangGraph',
  UIPATH:       'UiPath',
  CUSTOM_MCP:   'MCP (Model Context Protocol)',
  CUSTOM_REST:  'Custom REST',
};

const AUTH_METHODS = [
  { value: 'API_KEY', label: 'API Key' },
  { value: 'OAUTH2', label: 'OAuth2' },
  { value: 'SERVICE_ACCOUNT', label: 'Service Account' },
  { value: 'MTLS', label: 'mTLS' },
  { value: 'BEARER_TOKEN', label: 'Bearer Token' },
  { value: 'CUSTOM', label: 'Custom' },
];

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------
export default function GelPlatformConnectorsPage() {
  const { user } = useAuth();
  const { addToast } = useToast();

  // State management
  const [connectors, setConnectors] = useState([]);
  // Always use the full agent-building-platform list — never overwrite from backend
  const [platforms] = useState(Object.keys(PLATFORM_LABELS));
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTestingId, setIsTestingId] = useState(null);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [editingConnector, setEditingConnector] = useState(null);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // Form state — internal names match the UI labels, NOT backend fields
  const FORM_DEFAULTS = {
    platformType: '',
    name: '',
    endpointUrl: '',
    authMethod: 'API_KEY',
    authConfig: '{}',
    description: '',
    defaultModel: '',
    defaultMaxTokens: 2048,
    defaultTimeoutSeconds: 30,
    maxRequestsPerMinute: 60,
    maxConcurrentRequests: 10,
  };
  const [formData, setFormData] = useState(FORM_DEFAULTS);

  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterHealth, setFilterHealth] = useState('');
  const { page, pageSize, setPage } = usePaginationState(20);

  // ── Data Loading ─────────────────────────────────────────────────────────

  const loadConnectors = useCallback(async () => {
    try {
      setLoading(true);
      const list = unwrap(await api.gelListConnectors(user?.tenantId));
      // Exclude soft-deleted (DISABLED) connectors — from the user's perspective, delete means gone
      const active = Array.isArray(list)
        ? list.filter((c) => c.status !== 'DISABLED')
        : [];
      setConnectors(active);
    } catch (error) {
      console.error('Error loading connectors:', error);
      addToast('Failed to load connectors', 'error');
      setConnectors([]);
    } finally {
      setLoading(false);
    }
  }, [user?.tenantId, addToast]);

  useEffect(() => {
    loadConnectors();
  }, [loadConnectors]);

  // ── Filtering & Pagination ───────────────────────────────────────────────

  const filteredConnectors = useMemo(() => {
    return connectors.filter((conn) => {
      const matchesSearch =
        !searchTerm ||
        conn.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        conn.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPlatform = !filterPlatform || conn.platformType === filterPlatform;
      const matchesHealth = !filterHealth || conn.healthStatus === filterHealth;
      return matchesSearch && matchesPlatform && matchesHealth;
    });
  }, [connectors, searchTerm, filterPlatform, filterHealth]);

  const paginatedConnectors = useMemo(() => {
    const start = page * pageSize;
    return filteredConnectors.slice(start, start + pageSize);
  }, [filteredConnectors, page, pageSize]);

  const stats = useMemo(() => ({
    total: connectors.length,
    healthy: connectors.filter((c) => c.healthStatus === 'HEALTHY').length,
    degraded: connectors.filter((c) => c.healthStatus === 'DEGRADED').length,
    unhealthy: connectors.filter(
      (c) => c.healthStatus === 'UNHEALTHY' || (c.healthStatus !== 'HEALTHY' && c.healthStatus !== 'DEGRADED')
    ).length,
  }), [connectors]);

  // ── Form Handlers ────────────────────────────────────────────────────────

  // Draft support — lets the user close the modal and come back without losing work
  const [draftFormData, setDraftFormData] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);

  const resetForm = useCallback(() => {
    setFormData(FORM_DEFAULTS);
    setEditingConnector(null);
    setDraftFormData(null);
    setHasDraft(false);
  }, []);

  const handleSaveDraft = useCallback(() => {
    setDraftFormData({ ...formData });
    setHasDraft(true);
    setIsCreateModalOpen(false);
    addToast('Draft saved — click "+ New Connector" to resume', 'info');
  }, [formData, addToast]);

  const handleOpenCreate = useCallback(() => {
    if (hasDraft && draftFormData) {
      // Restore draft instead of starting fresh
      setFormData(draftFormData);
      setEditingConnector(null);
    } else {
      setFormData(FORM_DEFAULTS);
      setEditingConnector(null);
    }
    setIsCreateModalOpen(true);
  }, [hasDraft, draftFormData]);

  /** Clone an existing connector — pre-fills form with its data for quick duplication */
  const handleCloneConnector = useCallback(async (connector) => {
    try {
      setIsEditLoading(true);
      const raw = await api.gelGetConnector(connector.id);
      const entity = unwrap(raw);
      if (!entity || !entity.id) {
        addToast('Failed to load connector for cloning', 'error');
        return;
      }

      // Strip UI-managed fields from displayed authConfig (same as edit)
      const UI_FIELDS = ['defaultModel', 'defaultMaxTokens', 'defaultTimeoutSeconds'];
      const rawAuth = typeof entity.authConfig === 'string'
        ? JSON.parse(entity.authConfig || '{}')
        : (entity.authConfig || {});
      const cleanAuth = { ...rawAuth };
      UI_FIELDS.forEach((k) => delete cleanAuth[k]);

      // Pre-fill form but clear the editing state so it creates a NEW connector
      setEditingConnector(null);
      setFormData({
        platformType: entity.platformType || '',
        name: `${entity.name || 'Connector'} (Clone)`,
        endpointUrl: entity.endpointUrl || '',
        authMethod: entity.authMethod || 'API_KEY',
        authConfig: JSON.stringify(cleanAuth, null, 2),
        description: entity.description || '',
        defaultModel: rawAuth.defaultModel || '',
        defaultMaxTokens: rawAuth.defaultMaxTokens || 2048,
        defaultTimeoutSeconds: rawAuth.defaultTimeoutSeconds || 30,
        maxRequestsPerMinute: entity.maxRequestsPerMinute || 60,
        maxConcurrentRequests: entity.maxConcurrentRequests || 10,
      });
      setIsCreateModalOpen(true);
    } catch (error) {
      console.error('Error cloning connector:', error);
      addToast('Failed to clone connector', 'error');
    } finally {
      setIsEditLoading(false);
    }
  }, [addToast]);

  /** Populate form from backend entity — maps backend field names to form state */
  const handleOpenEdit = useCallback(async (connector) => {
    try {
      setIsEditLoading(true);
      const raw = await api.gelGetConnector(connector.id);
      /* Never log this entity: authConfig carries credential material. */
      const entity = unwrap(raw);
      if (!entity || !entity.id) {
        addToast('Failed to load connector details — no entity returned', 'error');
        return;
      }

      // Separate UI-managed fields from the raw auth credentials the user edits.
      // The backend stores defaultModel/defaultMaxTokens/defaultTimeoutSeconds
      // inside the authConfig JSON, but the user should never see or touch them
      // there — they have their own form fields.
      const UI_FIELDS = ['defaultModel', 'defaultMaxTokens', 'defaultTimeoutSeconds'];
      const rawAuth = typeof entity.authConfig === 'string'
        ? JSON.parse(entity.authConfig || '{}')
        : (entity.authConfig || {});

      // Extract UI values before stripping
      const uiDefaultModel = rawAuth.defaultModel || '';
      const uiDefaultMaxTokens = rawAuth.defaultMaxTokens || 2048;
      const uiDefaultTimeoutSeconds = rawAuth.defaultTimeoutSeconds || 30;

      // Build a clean auth object for the user to edit (credentials only)
      const cleanAuth = { ...rawAuth };
      UI_FIELDS.forEach((k) => delete cleanAuth[k]);
      const authConfigStr = JSON.stringify(cleanAuth, null, 2);

      setEditingConnector(entity);
      setFormData({
        platformType: entity.platformType || '',
        name: entity.name || '',
        endpointUrl: entity.endpointUrl || '',
        authMethod: entity.authMethod || 'API_KEY',
        authConfig: authConfigStr,
        description: entity.description || '',
        defaultModel: uiDefaultModel,
        defaultMaxTokens: uiDefaultMaxTokens,
        defaultTimeoutSeconds: uiDefaultTimeoutSeconds,
        maxRequestsPerMinute: entity.maxRequestsPerMinute || 60,
        maxConcurrentRequests: entity.maxConcurrentRequests || 10,
      });
      setIsCreateModalOpen(true);
    } catch (error) {
      console.error('Error loading connector:', error);
      addToast('Failed to load connector details', 'error');
    } finally {
      setIsEditLoading(false);
    }
  }, [addToast]);

  const handleFormChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  /** Build backend payload from form state — maps form names → backend field names */
  const handleSaveConnector = useCallback(async () => {
    if (!formData.platformType || !formData.name || !formData.endpointUrl) {
      addToast('Platform, Name, and Base URL are required', 'error');
      return;
    }

    let authConfig;
    try {
      authConfig = JSON.parse(formData.authConfig);
    } catch {
      addToast('Invalid JSON in Auth Config', 'error');
      return;
    }

    // Nest UI-only fields inside authConfig for connector-level storage
    const enrichedAuthConfig = { ...authConfig };
    if (formData.defaultModel) enrichedAuthConfig.defaultModel = formData.defaultModel;
    if (formData.defaultMaxTokens) enrichedAuthConfig.defaultMaxTokens = parseInt(formData.defaultMaxTokens, 10);
    if (formData.defaultTimeoutSeconds) enrichedAuthConfig.defaultTimeoutSeconds = parseInt(formData.defaultTimeoutSeconds, 10);

    const payload = {
      name: formData.name,
      endpointUrl: formData.endpointUrl,
      authMethod: formData.authMethod,
      authConfig: enrichedAuthConfig,
      description: formData.description || `${formData.name} — ${getPlatformLabel(formData.platformType)} connector`,
      maxConcurrentRequests: parseInt(formData.maxConcurrentRequests, 10) || 10,
      maxRequestsPerMinute: parseInt(formData.maxRequestsPerMinute, 10) || 60,
    };

    try {
      if (editingConnector) {
        // UpdateConnectorRequest does NOT accept platformType — only send it on create
        await api.gelUpdateConnector(editingConnector.id, payload);
        addToast('Connector updated successfully', 'success');
      } else {
        await api.gelCreateConnector({ platformType: formData.platformType, ...payload });
        addToast('Connector created successfully', 'success');
      }
      setIsCreateModalOpen(false);
      // After creating (not editing), clear filters so the new connector is visible
      if (!editingConnector) {
        setFilterPlatform('');
        setFilterHealth('');
        setSearchTerm('');
      }
      resetForm();
      loadConnectors();
    } catch (error) {
      console.error('Error saving connector:', error);
      addToast(error?.message || 'Failed to save connector', 'error');
    }
  }, [formData, editingConnector, addToast, resetForm, loadConnectors]);

  const handleOpenDelete = useCallback((id) => {
    setDeleteTargetId(id);
    setIsDeleteModalOpen(true);
  }, []);

  const handleDeleteConnector = useCallback(async () => {
    try {
      await api.gelDeleteConnector(deleteTargetId);
      addToast('Connector removed successfully', 'success');
      setIsDeleteModalOpen(false);
      setDeleteTargetId(null);
      loadConnectors();
    } catch (error) {
      console.error('Error deleting connector:', error);
      addToast('Failed to delete connector', 'error');
    }
  }, [deleteTargetId, addToast, loadConnectors]);

  const handleTestConnector = useCallback(async (id) => {
    try {
      setIsTestingId(id);
      const result = unwrap(await api.gelTestConnector(id));
      const status = result?.status || result?.healthStatus || 'UNKNOWN';
      const latency = result?.latencyMs ?? result?.latency ?? 0;
      addToast(
        `Health check: ${status} (${latency}ms)`,
        status === 'HEALTHY' ? 'success' : 'warning'
      );
      loadConnectors();
    } catch (error) {
      console.error('Error testing connector:', error);
      addToast(error?.message || 'Health check failed', 'error');
    } finally {
      setIsTestingId(null);
    }
  }, [addToast, loadConnectors]);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const getPlatformLabel = (platformType) => PLATFORM_LABELS[platformType] || platformType;

  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'HEALTHY': return '#059669';
      case 'DEGRADED': return '#f59e0b';
      case 'UNHEALTHY': return '#dc2626';
      default: return '#a8a29e';
    }
  };

  const formatLastCheck = (timestamp) => {
    if (!timestamp) return 'Never';
    try { return new Date(timestamp).toLocaleString(); } catch { return 'Unknown'; }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <GovStyles />
      {/* Wrap PageHeader in the same gutter the body uses so the title
          doesn't ram against the breadcrumbs. 24px top + 32px sides
          mirrors PageContainer's tokens. */}
      <div style={{ padding: '24px 32px 0', maxWidth: 1600, margin: '0 auto' }}>
        <PageHeader
          title="Agent Framework"
          subtitle="External AI agent framework integrations (Vertex AI, Anthropic, CrewAI, LangChain, MCP, etc.) — the binding target for ExteLets"
          icon={Plug}
        />
      </div>
      <div style={{ padding: '0 32px 32px', maxWidth: 1600, margin: '0 auto', minHeight: '100vh' }}>

        {/* Stats Row */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 32, flexWrap: 'wrap' }}>
          <StatCard
            label="Total Connectors"
            value={stats.total}
            accent={ACCENT.slate}
          />
          <StatCard
            label="Healthy"
            value={stats.healthy}
            accent={ACCENT.emerald}
          />
          <StatCard
            label="Degraded"
            value={stats.degraded}
            accent={ACCENT.amber}
          />
          <StatCard
            label="Unhealthy"
            value={stats.unhealthy}
            accent={ACCENT.rose}
          />
        </div>

        {/* Toolbar: Search, Select, Filters */}
        <Card style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <SearchInput
              value={searchTerm}
              onChange={(val) => { setSearchTerm(val); setPage(0); }}
              placeholder="Search connectors..."
              maxWidth={360}
            />

            <select
              value={filterPlatform}
              onChange={(e) => { setFilterPlatform(e.target.value); setPage(0); }}
              style={{ ...inputBase, width: 'auto', minWidth: 140, cursor: 'pointer' }}
            >
              <option value="">All Platforms</option>
              {platforms.map((p) => (
                <option key={p} value={p}>{getPlatformLabel(p)}</option>
              ))}
            </select>

            <select
              value={filterHealth}
              onChange={(e) => { setFilterHealth(e.target.value); setPage(0); }}
              style={{ ...inputBase, width: 'auto', minWidth: 160, cursor: 'pointer' }}
            >
              <option value="">All Health Status</option>
              <option value="HEALTHY">Healthy</option>
              <option value="DEGRADED">Degraded</option>
              <option value="UNHEALTHY">Unhealthy</option>
              <option value="UNKNOWN">Unknown</option>
            </select>

            <RefreshButton onClick={loadConnectors} loading={loading} />

            <PrimaryButton onClick={handleOpenCreate} icon={Plus}>
              {hasDraft ? 'Resume Draft' : 'New Connector'}
            </PrimaryButton>
          </div>
        </Card>

        {/* Connectors Table */}
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center', color: S[500] }}>
              <RefreshCw size={32} style={{ margin: '0 auto 16px', animation: 'govSpin 1s linear infinite' }} />
              <p>Loading connectors...</p>
            </div>
          ) : paginatedConnectors.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: S[500] }}>
              <Zap size={32} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
              <p>{connectors.length === 0 ? 'No connectors yet' : 'No results match your filters'}</p>
            </div>
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: S[50] }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Name</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Platform</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Auth Method</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Health Status</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Last Check</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Status</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedConnectors.map((connector) => (
                    <tr key={connector.id} style={{ background: '#ffffff', borderBottom: `1px solid ${S[100]}` }}>
                      <td style={{ padding: '12px 16px', fontSize: 13 }}>
                        <div>
                          <strong>{connector.name}</strong>
                          {connector.endpointUrl && (
                            <div style={{
                              fontSize: 11, color: S[400], marginTop: 4,
                              fontFamily: "'IBM Plex Mono', monospace", wordBreak: 'break-all',
                            }}>
                              {connector.endpointUrl}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13 }}>
                        <span style={{
                          display: 'inline-block', padding: '4px 8px',
                          background: connector.platformType === 'CUSTOM_MCP' ? ACCENT.sky.bg : S[100],
                          color: connector.platformType === 'CUSTOM_MCP' ? ACCENT.sky.text : S[600],
                          border: connector.platformType === 'CUSTOM_MCP' ? `1px solid ${ACCENT.sky.border}` : 'none',
                          borderRadius: 4, fontSize: 12, fontWeight: 500,
                        }}>
                          {getPlatformLabel(connector.platformType)}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13 }}>
                        <span style={{
                          display: 'inline-block', padding: '4px 8px',
                          background: S[100], borderRadius: 4, fontSize: 12, fontWeight: 500, color: S[600],
                        }}>
                          {connector.authMethod}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{
                            width: 10, height: 10, borderRadius: '50%',
                            background: getHealthStatusColor(connector.healthStatus),
                          }} />
                          <span>{connector.healthStatus || 'UNKNOWN'}</span>
                        </div>
                        {connector.healthMessage && (
                          <div style={{ fontSize: 11, color: S[400], marginTop: 4 }}>
                            {connector.healthMessage}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {formatLastCheck(connector.lastHealthCheckAt)}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13 }}>
                        <Badge text={connector.status === 'ACTIVE' ? 'Active' : 'Disabled'} color={connector.status === 'ACTIVE' ? ACCENT.emerald.text : S[400]} bg={connector.status === 'ACTIVE' ? ACCENT.emerald.bg : S[100]} border={connector.status === 'ACTIVE' ? ACCENT.emerald.border : S[200]} />
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13 }}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleTestConnector(connector.id)}
                            disabled={isTestingId === connector.id}
                            className={isTestingId !== connector.id ? 'gov-btn-ghost' : ''}
                            style={{
                              ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 500,
                              background: 'transparent', color: S[600], border: `1px solid ${S[200]}`,
                              opacity: isTestingId === connector.id ? 0.6 : 1,
                              cursor: isTestingId === connector.id ? 'not-allowed' : 'pointer',
                            }}
                            title="Test health"
                          >
                            {isTestingId === connector.id
                              ? <RefreshCw size={12} style={{ animation: 'govSpin 1s linear infinite' }} />
                              : <CheckCircle size={12} />}
                          </button>
                          <button
                            onClick={() => handleOpenEdit(connector)}
                            disabled={isEditLoading}
                            className={isEditLoading ? '' : 'gov-btn-ghost'}
                            style={{
                              ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 500,
                              background: 'transparent', color: S[600], border: `1px solid ${S[200]}`,
                              opacity: isEditLoading ? 0.6 : 1,
                              cursor: isEditLoading ? 'not-allowed' : 'pointer',
                            }}
                            title="Edit"
                          >
                            <Edit2 size={12} />
                          </button>
                          <button
                            onClick={() => handleCloneConnector(connector)}
                            disabled={isEditLoading}
                            className={isEditLoading ? '' : 'gov-btn-ghost'}
                            style={{
                              ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 500,
                              background: 'transparent', color: S[600], border: `1px solid ${S[200]}`,
                              opacity: isEditLoading ? 0.6 : 1,
                              cursor: isEditLoading ? 'not-allowed' : 'pointer',
                            }}
                            title="Clone connector"
                          >
                            <Copy size={12} />
                          </button>
                          <button
                            onClick={() => handleOpenDelete(connector.id)}
                            style={{
                              ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 500,
                              background: ACCENT.rose.bg, color: ACCENT.rose.text, border: `1px solid ${ACCENT.rose.border}`,
                              borderRadius: 8,
                            }}
                            title="Delete"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              <div style={{ padding: 16, background: S[50], borderTop: `1px solid ${S[200]}` }}>
                <Pagination
                  totalItems={filteredConnectors.length}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={setPage}
                />
              </div>
            </>
          )}
        </Card>

        {/* ── Create / Edit Modal ──────────────────────────────────────── */}
        <Modal
          isOpen={isCreateModalOpen}
          title={editingConnector ? 'Edit Connector' : 'Create New Connector'}
          onClose={() => { setIsCreateModalOpen(false); }}
          wide
          actions={
            <div style={{ display: 'flex', gap: 8, width: '100%', justifyContent: 'space-between' }}>
              <div>
                {!editingConnector && (
                  <GhostButton onClick={handleSaveDraft} icon={Save}>
                    Save Draft
                  </GhostButton>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <GhostButton onClick={() => { setIsCreateModalOpen(false); resetForm(); }}>
                  Cancel
                </GhostButton>
                <PrimaryButton onClick={handleSaveConnector}>
                  {editingConnector ? 'Update' : 'Create'}
                </PrimaryButton>
              </div>
            </div>
          }
        >
          {/* Platform & Name */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 }}>Platform Type *</label>
              <select
                value={formData.platformType}
                onChange={(e) => handleFormChange('platformType', e.target.value)}
                style={{ ...inputBase, width: '100%' }}
              >
                <option value="">Select platform</option>
                {platforms.map((p) => (
                  <option key={p} value={p}>{getPlatformLabel(p)}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 }}>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                placeholder="e.g., Production Anthropic"
                style={inputBase}
              />
            </div>
          </div>

          {/* MCP info banner */}
          {formData.platformType === 'CUSTOM_MCP' && (
            <div style={{
              background: ACCENT.sky.bg, border: `1px solid ${ACCENT.sky.border}`, borderRadius: 8,
              padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start',
            }}>
              <Server size={18} style={{ color: ACCENT.sky.icon, marginTop: 2, flexShrink: 0 }} />
              <div style={{ fontSize: 12, color: ACCENT.sky.text, lineHeight: 1.5 }}>
                <strong>MCP Connector</strong> — Connects to an external agent exposed via the
                Model Context Protocol. Set the <strong>Endpoint URL</strong> to the MCP server address
                (e.g., <code style={{ background: S[100], padding: '1px 4px', borderRadius: 3 }}>http://localhost:3100/mcp</code>).
                The Agentlets protection pipeline will wrap all tool calls through protected envelopes.
              </div>
            </div>
          )}

          {/* Endpoint URL & Auth Method */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 }}>
                {formData.platformType === 'CUSTOM_MCP' ? 'MCP Server URL *' : 'Base URL *'}
              </label>
              <input
                type="url"
                value={formData.endpointUrl}
                onChange={(e) => handleFormChange('endpointUrl', e.target.value)}
                placeholder={formData.platformType === 'CUSTOM_MCP'
                  ? 'http://localhost:3100/mcp'
                  : 'https://api.example.com'}
                style={inputBase}
              />
            </div>
            <div style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 }}>Auth Method</label>
              <select
                value={formData.authMethod}
                onChange={(e) => handleFormChange('authMethod', e.target.value)}
                style={{ ...inputBase, width: '100%' }}
              >
                {AUTH_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Auth Config JSON */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 }}>Auth Config (JSON)</label>
            <textarea
              value={formData.authConfig}
              onChange={(e) => handleFormChange('authConfig', e.target.value)}
              placeholder={formData.platformType === 'CUSTOM_MCP'
                ? '{"sdkToken": "agt_sk_..."}'
                : '{"apiKey": "sk-..."}'}
              style={{
                ...inputBase, fontFamily: "'IBM Plex Mono', monospace", minHeight: 120, resize: 'vertical',
                fontSize: 12, lineHeight: 1.5, padding: '9px 12px',
              }}
            />
            <div style={{ fontSize: 11, color: S[400], marginTop: 4 }}>
              {formData.platformType === 'CUSTOM_MCP'
                ? 'Enter valid JSON — e.g. SDK token or bearer credentials for the MCP server'
                : 'Enter valid JSON with authentication credentials'}
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 }}>Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              placeholder={formData.platformType === 'CUSTOM_MCP'
                ? 'e.g., KYC Due Diligence MCP Bridge'
                : 'Brief description of this connector'}
              style={inputBase}
            />
          </div>

          {/* Default Model & Max Tokens */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 }}>
                {formData.platformType === 'CUSTOM_MCP' ? 'Tool Namespace / Model' : 'Default Model'}
              </label>
              <input
                type="text"
                value={formData.defaultModel}
                onChange={(e) => handleFormChange('defaultModel', e.target.value)}
                placeholder={formData.platformType === 'CUSTOM_MCP'
                  ? 'e.g., kyc-agent or leave blank'
                  : 'e.g., gemini-2.0-flash'}
                style={inputBase}
              />
            </div>
            <div style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 }}>Default Max Tokens</label>
              <input
                type="number"
                value={formData.defaultMaxTokens}
                onChange={(e) => handleFormChange('defaultMaxTokens', e.target.value)}
                min="1" max="100000"
                style={inputBase}
              />
            </div>
          </div>

          {/* Rate Limit & Timeout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 }}>Rate Limit (RPM)</label>
              <input
                type="number"
                value={formData.maxRequestsPerMinute}
                onChange={(e) => handleFormChange('maxRequestsPerMinute', e.target.value)}
                min="1" max="10000"
                style={inputBase}
              />
            </div>
            <div style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 }}>Default Timeout (seconds)</label>
              <input
                type="number"
                value={formData.defaultTimeoutSeconds}
                onChange={(e) => handleFormChange('defaultTimeoutSeconds', e.target.value)}
                min="1" max="600"
                style={inputBase}
              />
            </div>
          </div>

          {/* Max Concurrency */}
          <div style={{ marginBottom: 0 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 }}>Max Concurrency</label>
            <input
              type="number"
              value={formData.maxConcurrentRequests}
              onChange={(e) => handleFormChange('maxConcurrentRequests', e.target.value)}
              min="1" max="1000"
              style={inputBase}
            />
          </div>
        </Modal>

        {/* ── Delete Confirmation Modal ────────────────────────────────── */}
        <Modal
          isOpen={isDeleteModalOpen}
          title="Delete Connector"
          onClose={() => { setIsDeleteModalOpen(false); setDeleteTargetId(null); }}
          actions={
            <>
              <GhostButton onClick={() => { setIsDeleteModalOpen(false); setDeleteTargetId(null); }}>
                Cancel
              </GhostButton>
              <button onClick={handleDeleteConnector} style={{
                ...btnBase, padding: '8px 18px', fontSize: 12, fontWeight: 700,
                background: ACCENT.rose.bg, color: ACCENT.rose.text, border: `1px solid ${ACCENT.rose.border}`,
                borderRadius: 8,
              }}>Delete</button>
            </>
          }
        >
          <div style={{ color: S[900], lineHeight: 1.6 }}>
            <p>Are you sure you want to delete this connector? This action cannot be undone.</p>
            {deleteTargetId && (
              <div style={{ padding: 12, background: BRAND.light, borderRadius: 6, marginTop: 16, fontSize: 13 }}>
                <strong>Connector ID:</strong><br />
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>{deleteTargetId}</span>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </>

  );
}
